import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import { PageEditor } from "@/components/admin/pages/PageEditor";
import { PagesList } from "@/components/admin/pages/PagesList";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  getPageById,
  listAllRoutes,
  listPages,
  upsertPage,
  type UpsertPagePayload,
} from "@/services/pagesService";
import type { PageFull } from "@/types/page";

const PAGE_LIMIT = 20;

type RouteItem = { id: string; route: string; name: string };

type PendingRoute = RouteItem & { pending?: boolean };

export default function AdminPages() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdminAuth();

  const [pages, setPages] = useState<PageFull[]>([]);
  const [routes, setRoutes] = useState<RouteItem[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<PageFull | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin-login");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadRoutes();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadPages();
  }, [isAdmin, search, page]);

  useEffect(() => {
    if (!selectedPageId) {
      setSelectedPage(null);
      return;
    }

    void loadSelectedPage(selectedPageId);
  }, [selectedPageId]);

  const filteredRoutes: PendingRoute[] = useMemo(() => {
    if (!selectedPageId) {
      return routes;
    }

    const exists = routes.some(route => route.id === selectedPageId);
    if (exists) return routes;

    if (selectedPage) {
      return [...routes, { id: selectedPage.id, route: selectedPage.route, name: selectedPage.name, pending: true }];
    }

    return routes;
  }, [routes, selectedPageId, selectedPage]);

  async function loadPages() {
    try {
      setLoadingList(true);
      const offset = (page - 1) * PAGE_LIMIT;
      const result = await listPages({ search, limit: PAGE_LIMIT, offset });
      setPages(result.pages);
      setTotal(result.total);
    } catch (error: any) {
      console.error("Erro ao carregar páginas", error);
      toast.error(error.message ?? "Não foi possível carregar as páginas.");
    } finally {
      setLoadingList(false);
    }
  }

  async function loadRoutes() {
    try {
      setLoadingRoutes(true);
      const data = await listAllRoutes();
      setRoutes(data);
    } catch (error: any) {
      console.error("Erro ao carregar rotas", error);
      toast.error(error.message ?? "Não foi possível carregar as rotas disponíveis.");
    } finally {
      setLoadingRoutes(false);
    }
  }

  async function loadSelectedPage(id: string) {
    try {
      const pageData = pages.find(item => item.id === id) ?? (await getPageById(id));
      if (pageData) {
        setSelectedPage(pageData);
      }
    } catch (error: any) {
      console.error("Erro ao carregar página", error);
      toast.error(error.message ?? "Não foi possível carregar a página selecionada.");
    }
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handleCreateNew() {
    setSelectedPageId(null);
    setSelectedPage(null);
  }

  async function handleSave(payload: UpsertPagePayload) {
    try {
      setSaving(true);
      const result = await upsertPage(payload);
      toast.success("Página atualizada com sucesso!");
      setSelectedPageId(result.id);
      setSelectedPage(result);
      await Promise.all([loadPages(), loadRoutes()]);
    } catch (error: any) {
      console.error("Erro ao salvar página", error);
      toast.error(error.message ?? "Não foi possível salvar as alterações.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setSelectedPageId(null);
    setSelectedPage(null);
  }

  if (authLoading || !isAdmin) {
    return null;
  }

  return (
    <AdminLayout
      title="Páginas"
      description="Gerencie rotas, publicação e metadados SEO do ecossistema Toc Toc."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Conteúdo" },
        { label: "Páginas" },
      ]}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] xl:gap-6">
          <PagesList
            pages={pages}
            total={total}
            limit={PAGE_LIMIT}
            page={page}
            search={search}
            loading={loadingList}
            onPageChange={setPage}
            onSearchChange={handleSearchChange}
            onEdit={pageData => {
              setSelectedPageId(pageData.id);
              setSelectedPage(pageData);
            }}
            onCreateNew={handleCreateNew}
            selectedPageId={selectedPageId}
          />

          <PageEditor
            selectedPage={selectedPage}
            routes={filteredRoutes}
            onSelectPage={id => setSelectedPageId(id)}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
            loadingRoutes={loadingRoutes}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
